<template>
  <div class="images-view">
    <div v-if="aiEnabled" class="row mb-2">
      <div class="col-md-12 d-flex align-items-start justify-content-between">
        <div class="d-flex flex-column">
          <b-button
            :class="aiSelectMode ? '' : 'btn-ai-generate'"
            :variant="aiSelectMode ? 'warning' : 'primary'"
            @click="toggleAiSelectMode"
            :disabled="disabled"
            style="align-self: flex-start"
          >
            <font-awesome-icon :icon="aiSelectMode ? 'times' : 'magic'" class="mr-1"/>
            {{ aiSelectMode ? $t('Cancel Selection') : $t('Generate AI Images') }}
          </b-button>
          <span v-if="aiSelectMode" class="mt-1 text-muted small">
            {{ $t('Select images to use as source, then click Generate') }}
          </span>
        </div>
        <b-button
          v-if="aiSelectMode && selectedImageUuids.length"
          size="sm"
          variant="primary"
          @click="openAiModal"
        >
          <font-awesome-icon icon="bolt" class="mr-1"/>
          {{ $t('Generate new Images') }}
        </b-button>
      </div>
    </div>

    <div v-if="aiSelectMode && aiEnabled" class="row mb-2">
      <div class="col-md-12">
        <div class="d-flex flex-wrap ai-image-selector">
          <div
            v-for="img in uploadedImages"
            :key="img.uuid"
            class="ai-select-item mr-2 mb-2"
            :class="{'ai-select-item--selected': selectedImageUuids.includes(img.uuid)}"
            @click="toggleImageSelection(img.uuid)"
          >
            <img :src="getImageThumbnail(img)" :alt="img.name" class="rounded"/>
            <div class="ai-select-overlay">
              <font-awesome-icon :icon="selectedImageUuids.includes(img.uuid) ? 'check-circle' : 'circle'" class="ai-select-icon"/>
            </div>
          </div>
        </div>
        <p v-if="!uploadedImages.length" class="text-muted small">
          {{ $t('No uploaded images available for AI generation.') }}
        </p>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <file-uploader :accept="fileUploaderAccept" ref="imageUploader" :items-prop="images" :in-progress="inProgress"
                       :uploading-progress="uploadingProgress" :uploading-progress-value="uploadingProgressValue"
                       @onImageDragChange="onImageDragChange" :enable-image-edit="enableImageEdit"
                       :hide-action-buttons="!$hasPermission(changePermission) || disabled"
                       :hide-delete-button="!$hasPermission(deletePermission) || disabled"
                       :hide-upload-button="!$hasPermission(changePermission) || disabled"
                       :hide-image-actions="!$hasPermission(changePermission) || disabled"
                       :disable-image-drag-upload="!$hasPermission(changePermission) || disabled"
                       :is-draggable="$hasPermission(changePermission) || disabled"
                       :enable-image-resize="true" enable-picture-taking/>
      </div>
    </div>
    <div class="row" v-if="!hideSaveBtn && !disabled">
      <div v-permission="changePermission" class="col-md-12 text-right">
        <submit-button :show-loader="inProgress" :disabled="inProgress" @click="save()" class="mt-2">
          {{ $t('Save') }}
        </submit-button>
      </div>
    </div>

    <ai-image-generation-modal
      v-if="aiEnabled && inventoryUuid"
      :modal-id="aiModalId"
      :inventory-uuid="inventoryUuid"
      :source-images="selectedImages"
      @confirmed="onAiImageConfirmed"
      @hidden="onAiModalHidden"
    />
  </div>
</template>

<script>

  import {createNamespacedHelpers} from "vuex";
  import InventoryService from "../../InventoryService";
  import SubmitButton from "../../../../core/components/common/SubmitButton";
  import FileUploader from "@/core/components/file-uploader/FileUploader";
  import AiImageGenerationModal from "./AiImageGenerationModal";
  import {SOURCE_INVENTORY_TABLE} from "@/modules/constants";
  import i18n from "@/shared/i18n";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "ImagesView",
    components: {FileUploader, SubmitButton, AiImageGenerationModal},
    props: {
      inventoryUuid: {
        type: String,
        required: false,
      },
      hideSaveBtn: {
        type: Boolean,
        default: false
      },
      runPollingOnSave: {
        type: Boolean,
        default: true
      },
      changePermission: {
        type: [Boolean, String],
        default: false
      },
      deletePermission: {
        type: [Boolean, String],
        default: false
      },
      source: {
        type: String,
        required: false
      },
      enableImageEdit: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      preserveDeletedUuids: {
        type: Boolean,
        default: false
      },
      department: {
        type: Object,
        default: null,
      },
    },
    data: () => {
      return {
        inProgress: false,
        uploadingProgressValue: 0,
        uploadingProgress: false,
        pollingInterval: null,
        aiSelectMode: false,
        selectedImageUuids: [],
        aiModalId: 'ai-image-generation-modal',
      };
    },
    computed: {
      ...mapStateGlobal(['videoFileFormats', 'videoMaxFileSize', 'imageFileFormats', 'imageMaxFileSize']),
      ...mapState({
        images: state => state.view.images,
      }),
      fileUploaderAccept() {
        return [
          {formats: this.videoFileFormats, maxSize: this.videoMaxFileSize},
          {formats: this.imageFileFormats, maxSize: this.imageMaxFileSize},
        ]
      },
      aiEnabled() {
        return this.department !== null && !!this.department.ai_image_generation_enabled
      },
      uploadedImages() {
        return this.images.filter(img => img.uuid && !img.to_be_deleted)
      },
      selectedImages() {
        return this.uploadedImages.filter(img => this.selectedImageUuids.includes(img.uuid))
      },
    },
    watch: {
      inventoryUuid() {
        if (this.images.length === 0) {
          this.initImages();
        }
      },
      aiEnabled(val) {
        if (!val) {
          this.aiSelectMode = false
          this.selectedImageUuids = []
        }
      },
    },
    methods: {
      ...mapActions(['getImages', 'updateImages', 'emptyImages', 'updateListInventoryImages', 'removeListInventoryImagesByUuids', 'updateDeletedImages']),
      getImageThumbnail(img) {
        if (!img) return ''
        if (img.preview_url) return img.preview_url
        const data = img.data || {}
        for (const size of ['thumb', 'sm', 'original', 'lg', 'xlg']) {
          if (data[size] && data[size].url) return data[size].url
        }
        return ''
      },
      toggleAiSelectMode() {
        this.aiSelectMode = !this.aiSelectMode
        if (!this.aiSelectMode) {
          this.selectedImageUuids = []
        }
      },
      toggleImageSelection(uuid) {
        const idx = this.selectedImageUuids.indexOf(uuid)
        if (idx === -1) {
          this.selectedImageUuids.push(uuid)
        } else {
          this.selectedImageUuids.splice(idx, 1)
        }
      },
      openAiModal() {
        this.$bvModal.show(this.aiModalId)
      },
      onAiImageConfirmed(img) {
        this.images.push(img)
      },
      onAiModalHidden() {
        this.aiSelectMode = false
        this.selectedImageUuids = []
      },
      init() {
        if (this.images.length > 0) {
          this.images.forEach((image) => {
            image.file = null;
            image.to_be_deleted = false
          });
          this.$refs.imageUploader.activeItemIndex = 0;
          this.runPolling(this.inventoryUuid);
        }
      },
      runPolling(inventoryUuid) {
        // TODO may or even should implement two user iteraction at the same time. Should be discussed

        const needsPolling = this.images.find((item) => item.processing === true);

        if (!needsPolling) {
          return false;
        }

        this.inProgress = true;

        this.pollingInterval = setInterval(async () => {
          let images = (await InventoryService.getImages(inventoryUuid)).body;
          let shouldStopPolling = true;

          images.forEach((image) => {
            let item = this.images.find((item) => item.uuid === image.uuid);
            item.processing = image.processing;
            item.data = image.data;

            if (item.processing) {
              shouldStopPolling = false;
            }
          });

          if (shouldStopPolling) {
            this.$toast('success', i18n.t('Images were successfully updated'));
            this.inProgress = false;
            clearInterval(this.pollingInterval);
          }
        }, 2000);

      },
      clearPollingInterval() {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
        }
      },
      onImageDragChange(data) {
        this.updateImages(data)
      },
      async save(inventoryUuid = null) {
        this.inProgress = true
        if (!inventoryUuid) {
          inventoryUuid = this.inventoryUuid;
        }

        this.uploadingProgress = true;
        const progressEvent = (total, finished) => {
          this.uploadingProgressValue = parseInt("" + Math.round((finished / total) * 100));
        }
        let success, images = [], body, errorsList = []
        try {
          if (this.preserveDeletedUuids) {
            this.updateDeletedImages(this.images.filter(i => i.to_be_deleted && i.uuid).map(i => i.uuid))
          }
          const res = await InventoryService.createRenameAndDeleteImages(inventoryUuid, this.images, '/api/v1/inventories/images/', progressEvent)
          success = res.success
          images = res.images
          body = res.body
        } catch (err) {
          success = false
          images = null;
          body = err.body
          errorsList = err.errorsList
        }

        if (success) {
          // Removing all elements from images which do not have uuid (not uploaded ones)
          // noinspection ES6MissingAwait
          this.updateImages([]);

          // Pushing newly uploaded images to image array
          images.forEach((image) => {
            image.file = null;
            this.images.push(image);
          });

          if (this.source === SOURCE_INVENTORY_TABLE) {
            // noinspection JSCheckFunctionSignatures
            // noinspection ES6MissingAwait
            this.updateListInventoryImages({uuid: inventoryUuid, images,});
          }

          this.$refs.imageUploader.activeItemIndex = 0;
        } else {
          if (body instanceof Object && body['non_field_errors']) {
            this.$errorDialog(body['non_field_errors'][0]);
          } else if (body instanceof Array) {
            const listImagesArr = [];
            body.filter(i => i.image).map(e => listImagesArr.push(this.$createElement('li', {}, e.image[0])))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listImagesArr))
          } else if (errorsList instanceof Array && errorsList.length > 0) {
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, errorsList))
          } else {
            this.$errorDialog(this.$i18n.t('Something went wrong when creating images'))
          }
        }

        if (this.runPollingOnSave) {
          this.runPolling(inventoryUuid);
        }

        this.uploadingProgressValue = 0;
        this.uploadingProgress = false;
        this.inProgress = false;
        return true;
      },
      async initImages() {
        if (this.inventoryUuid) {
          await this.getImages(this.inventoryUuid);
          this.init();
        }
      },
      clearImages() {
        this.updateImages([]);
      }
    },
    beforeDestroy() {
      this.clearPollingInterval()
    },
    async beforeMount() {
      this.clearImages();
      this.initImages();
    }
  }

</script>

<style lang="scss" scoped>
  @import "./../../scss/variables";

  .btn-ai-generate {
    background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
    border: none;
    color: #fff;
    font-weight: 600;
    letter-spacing: 0.3px;
    box-shadow: 0 3px 12px rgba(101, 60, 220, 0.4);
    transition: box-shadow 0.2s, transform 0.15s;

    &:hover, &:focus {
      background: linear-gradient(135deg, #7b1fe0 0%, #3a87fd 100%);
      box-shadow: 0 5px 18px rgba(101, 60, 220, 0.55);
      transform: translateY(-1px);
      color: #fff;
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(101, 60, 220, 0.35);
    }
  }

  .ai-image-selector {
    min-height: 40px;
  }

  .ai-select-item {
    position: relative;
    cursor: pointer;
    border: 2px solid transparent;
    border-radius: 4px;
    transition: border-color 0.15s;

    img {
      width: 64px;
      height: 64px;
      object-fit: cover;
      display: block;
    }

    &:hover {
      border-color: #007bff;
    }

    &--selected {
      border-color: #28a745;

      .ai-select-overlay {
        background: rgba(40, 167, 69, 0.2);
      }
    }
  }

  .ai-select-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
  }

  .ai-select-icon {
    font-size: 1.4rem;
    color: #28a745;
    filter: drop-shadow(0 0 2px #fff);
  }
</style>
